import { Close, Link } from '@carbon/icons-react'
import { DDSInput, DDSTextArea, DDSTypography } from 'den-design-system'
import React, { useEffect } from 'react'
import _, { isNull } from 'lodash'
import { createSearchParams, useNavigate, useParams } from 'react-router-dom'
import {
  getAnIncident,
  updateAnIncidentFormData
} from '../../api/incident/incident.service'

import { AccessRoleConstants } from '../../constants/AccessRoleConstants'
import { Button } from '../../components/New/Button/Button'
import DatePicker from '../../components/New/Datepicker/Datepicker'
import DocumentHeader from '../../components/Helmet/DocumentHeader'
import { HeaderConstants } from '../../constants/HeaderConstants'
import IncidentConstants from '../../constants/IncidentConstants'
import Joi from 'joi'
import MultiMedia from './MediaUpload'
import RegexConstants from '../../constants/RegexConstants'
import SecureComponent from '../../components/Layout/SecureComponent'
import Select from '../../components/New/Select/Select'
import Spinner from '../../components/common/Spinner/Spinner'
import { ToastMessageConstants } from '../../constants/ToastMessageConstants'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import dayjs from 'dayjs'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import pallete from '../../global/newPallete'
import { parseFileInfoFromUrl } from '../../utils/IncidentUtil'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { useState } from 'react'
import { useStoreContext } from '../../store/StoreContext'

interface MediaFile {
  id: string
  file: File
  fileUrl: string
}

const UpdateIncident = () => {
  const store = useStoreContext()
  const [addButtonDisabled, setAddButtonDisabled] = useState(false)
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([])

  const [reference, setReference] = useState<string>('')

  const [loading, setLoading] = useState<boolean>(false)
  const [incidentDetails, setIncidentDetails] = useState<any>(null)
  const [createButtonDisabled, setCreateButtonDisabled] = useState(false)
  const [priorityValue, setPriorityValue] = useState<string>('P1')

  const { incidentId } = useParams()
  const [isFormValid, setIsFormValid] = useState(false)
  const [originalData, setOriginalData] = useState<any>(null)
  const [hasFormChanged, setHasFormChanged] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [filesToRemove, setFilesToRemove] = useState<string[]>([])

  const navigate = useNavigate()

  const [totalSize, setTotalSize] = useState(0)

  useEffect(() => {
    if (incidentDetails?.attachedFiles?.length) {
      const collectedFileInfos = incidentDetails.attachedFiles.map(
        (media: any) => {
          const fileInfo = parseFileInfoFromUrl(media)
          return fileInfo
        }
      )
      const totalSize = collectedFileInfos.reduce(
        (sum: any, file: any) => sum + Number(file.size),
        0
      )

      setTotalSize(totalSize)
    }
  }, [incidentDetails?.attachedFiles])

  useEffect(() => {
    fetchAnIncident()
  }, [incidentId])

  useEffect(() => {
    if (incidentDetails?.priorityStatus)
      setPriorityValue(incidentDetails?.priorityStatus)
  }, [incidentDetails?.priorityStatus])

  const projects = [
    {
      label: (
        <div>
          <span style={{ color: pallete.colors.warningRed1 }}>P0</span> -
          Critical
        </div>
      ),
      value: 'P0'
    },
    {
      label: (
        <div>
          <span style={{ color: pallete.colors.warningRed2 }}>P1</span> - High
        </div>
      ),
      value: 'P1'
    },
    {
      label: (
        <div>
          <span style={{ color: pallete.colors.warningRed3 }}>P2</span> -
          Moderate
        </div>
      ),
      value: 'P2'
    },
    {
      label: (
        <div>
          <span style={{ color: pallete.colors.warningBlue }}>P3</span> - Low
        </div>
      ),
      value: 'P3'
    }
  ]

  const schema = Joi.object({
    priorityStatus: Joi.string().required().label('Priority Status'),
    identifiedOn: Joi.date().required().label('Identified On'),
    shortDescription: Joi.string()
      .max(150)
      .required()
      .label('Short Description'),
    longDescription: Joi.string().required().label('Long Description')
  })

  useEffect(() => {
    if (incidentDetails && originalData) {
      // Validate the current form values
      const { error } = schema.validate(
        {
          priorityStatus: incidentDetails.priorityStatus,
          identifiedOn: incidentDetails.identifiedOn,
          shortDescription: incidentDetails.shortDescription,
          longDescription: incidentDetails.longDescription
        },
        { abortEarly: false }
      )

      const hasFormChanged =
        originalData.priorityStatus !== incidentDetails.priorityStatus ||
        originalData.identifiedOn !== incidentDetails.identifiedOn ||
        originalData.shortDescription !== incidentDetails.shortDescription ||
        originalData.longDescription !== incidentDetails.longDescription ||
        !_.isEqual(
          originalData.referenceLinks,
          incidentDetails.referenceLinks
        ) ||
        !_.isEqual(originalData.attachedFiles, incidentDetails.attachedFiles)

      if (error || !hasFormChanged) {
        setIsButtonDisabled(true)
      } else {
        setIsButtonDisabled(false)
      }
    } else {
      setIsButtonDisabled(true)
    }
  }, [incidentDetails, originalData])

  const fetchAnIncident = () => {
    setLoading(true)

    getAnIncident({ $incidentId: incidentId })
      .then((response: any) => {
        const incidentData = response.data

        const data = {
          incidentId: incidentData.id,
          priorityStatus: incidentData.priority,
          identifiedOn: incidentData.identifiedOn,
          shortDescription: incidentData.briefDescription,
          longDescription: incidentData.longDescription,
          referenceLinks: incidentData.savedLinks as string[],
          incidentNumber: incidentData.incidentNumber,
          attachedFiles: incidentData.attachedFiles
        }

        setOriginalData(data)
        setIncidentDetails(data)
      })
      .catch((error) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.INCIDENT.GET_ALL_INCIDENTS.ERROR
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleUpdateIncident = () => {
    if (!isButtonDisabled) {
      setLoading(true)
      const formData = new FormData()

      formData.append('$incidentId', String(incidentId))
      formData.append('priority', incidentDetails.priorityStatus)
      formData.append('identifiedOn', incidentDetails.identifiedOn)
      formData.append('briefDescription', incidentDetails.shortDescription)
      formData.append('longDescription', incidentDetails.longDescription)
      formData.append(
        'savedLinks',
        JSON.stringify(incidentDetails.referenceLinks)
      )
      if (filesToRemove !== null) {
        formData.append('filesToRemove', JSON.stringify(filesToRemove))
      }
      mediaFiles?.forEach((file: any) => {
        if (file.file) {
          formData.append('files', file.file)
        }
      })

      updateAnIncidentFormData(formData)
        .then(() => {
          ToastNotification({
            type: 'success',
            message: ToastMessageConstants.INCIDENT.UPDATE_AN_INCIDENT.SUCCESS
          })

          const route = replaceUrl(
            routeConstants.MANAGE_INCIDENTS,
            store.scopeStore.getScope()['organization-id'] as string,
            store.scopeStore.getScope()['project-id'] as string,
            store.scopeStore.getScope()['environment-id'] as string
          ).replace(':incidentId', String(incidentId))
          navigate({
            pathname: route,
            search: `?${createSearchParams({ tab: '1' })}`
          })
        })
        .catch(() => {
          ToastNotification({
            type: 'error',
            message: ToastMessageConstants.INCIDENT.UPDATE_AN_INCIDENT.ERROR
          })
        })
        .finally(() => setLoading(false))
    } else {
      ToastNotification({
        type: 'error',
        message: ToastMessageConstants.INCIDENT.REQUIRED_FIELD.ERROR
      })
      store.uiStore.setGlobalLoader(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isNull(store.incidentStore.getIncidentData())) {
      fetchAnIncident()
    } else {
      setIncidentDetails(store.incidentStore.getIncidentData())
    }
  }, [store.incidentStore.getIncidentData()])

  useEffect(() => {
    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Incidents',
        link:
          replaceUrl(
            routeConstants.INCIDENT,
            store.scopeStore.getScope()['organization-id'] as string,
            store.scopeStore.getScope()['project-id'] as string,
            store.scopeStore.getScope()['environment-id'] as string
          ) + '?page=1&tab=1'
      },
      {
        label: incidentDetails?.incidentNumber,
        link: replaceUrl(
          routeConstants.MANAGE_INCIDENTS,
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        ).replace(':incidentId', String(incidentId))
      },
      {
        label: 'Update an Incident',
        link: replaceUrl(
          routeConstants.UPDATE_INCIDENT,
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      }
    ])
  }, [store.breadcrumbStore.getBreadcrumbsOptions(), incidentDetails])

  const getMaxDate = () => {
    const orgTimezone = store.userStore.getOrganizationPreference().timeZone

    const timezoneString = moment()
      .tz(orgTimezone)
      .format('YYYY-MM-DDTHH:mm:ss')

    const maxDate = new Date(timezoneString)
    return dayjs(maxDate)
  }

  return (
    <>
      <DocumentHeader header={HeaderConstants.INCIDENT} />
      {loading || store.uiStore.getGlobalLoader() ? (
        <Spinner />
      ) : (
        <div className='gap-[4px] flex flex-col  justify-between h-full'>
          <div className='py-[24px]'>
            <DDSTypography.Title
              variant='semiBold'
              type='h3'
              color={pallete.colors.textDark3}
              className='gap-[32px]'
            >
              {IncidentConstants.REPORT_AN_INCIDENT}
            </DDSTypography.Title>
            <div className='pt-[32px] gap-x-[52px] flex flex-row'>
              <div className='w-[65%]'>
                <div className='flex flex-row gap-[24px] w-[100%] incident-select '>
                  <div className='flex items-center'>
                    <Select
                      size='large'
                      label='Priority'
                      id='update-priority'
                      required
                      value={priorityValue}
                      name='projects'
                      options={projects}
                      searchable={false}
                      placeholder='Choose'
                      onSelect={(value) => {
                        setIncidentDetails({
                          ...incidentDetails,
                          priorityStatus: value
                        })
                      }}
                    />
                  </div>
                  <div className='override-dds-input-field-incident-page  flex items-center'>
                    <DatePicker
                      id='update-incident-datepicker'
                      value={dayjs(incidentDetails?.identifiedOn)}
                      name='Identified on'
                      required
                      format='MM/dd/YYYY'
                      disabled={true}
                      inputWidth={`${store.uiStore.getSidebarExpanded() ? '370px' : '490px'}`}
                      inputHeight='32px'
                      label='Identified on'
                      maxDate={getMaxDate()}
                      shape='roundedRectangle'
                      placeholder='Select Date'
                      onChange={(date: any) => {
                        setIncidentDetails({
                          ...incidentDetails,
                          identifiedOn: moment(date).toISOString()
                        })
                      }}
                    />
                  </div>
                </div>
                <div className='pt-[32px]'>
                  <DDSInput
                    name='Title'
                    label='Title'
                    required
                    maxLength={150}
                    height='32px'
                    shape='roundedRectangle'
                    type='text'
                    onChange={(e) => {
                      setIncidentDetails({
                        ...incidentDetails,
                        shortDescription: e.target.value
                      })
                    }}
                    style={{
                      borderRadius: 4,
                      width: '100%'
                    }}
                    value={incidentDetails?.shortDescription}
                    validateInput={true}
                    variant='plain'
                    onError={(error: any) => {
                      setCreateButtonDisabled(error)
                    }}
                  />
                </div>
                <div className='pt-[32px] override-textarea-border-radius'>
                  <DDSTextArea
                    name='Description'
                    label='Description'
                    minRow={5}
                    maxLength={2000}
                    required={true}
                    style={{
                      borderRadius: '6px',
                      width: '100%'
                    }}
                    value={incidentDetails?.longDescription}
                    onChange={(e: any) => {
                      setIncidentDetails({
                        ...incidentDetails,
                        longDescription: e.target.value
                      })
                    }}
                  />
                </div>
                <div className='!w-[100%] flex pt-[32px] flex-row align-center gap-[24px]'>
                  <div className='!w-[100%] reference-link'>
                    <DDSInput
                      label='Reference Links'
                      shape='roundedRectangle'
                      placeholder='Paste your link here'
                      type='text'
                      height='32px'
                      onChange={(e) => {
                        setReference(e.target.value)
                        if (
                          incidentDetails.referenceLinks.includes(
                            e.target.value
                          ) ||
                          !RegexConstants.REFERENCE_REGEX.test(e.target.value)
                        ) {
                          setAddButtonDisabled(true)
                        } else {
                          setAddButtonDisabled(false)
                        }
                      }}
                      validationRegex={
                        new RegExp(RegexConstants.REFERENCE_REGEX)
                      }
                      validate={(value: string) => {
                        if (
                          RegexConstants.REFERENCE_REGEX.test(value) &&
                          incidentDetails?.referenceLinks?.includes(value)
                        ) {
                          return {
                            error: true,
                            message: 'Reference link already exists'
                          }
                        } else if (value === '') {
                          return {
                            error: false,
                            message: ''
                          }
                        } else if (
                          !RegexConstants.REFERENCE_REGEX.test(value)
                        ) {
                          return {
                            error: true,
                            message: 'Please enter a valid reference link'
                          }
                        }
                        return {
                          error: false,
                          message: ''
                        }
                      }}
                      value={reference}
                      name='referenceLinks'
                      validateInput={true}
                      variant='plain'
                    />
                  </div>
                  <div className='incident-add-button my-[28px]'>
                    <Button
                      id='add-links'
                      disabled={reference?.length == 0 || addButtonDisabled}
                      size='medium'
                      label='Add Link'
                      onClick={() => {
                        if (
                          !incidentDetails?.referenceLinks.includes(reference)
                        ) {
                          setIncidentDetails({
                            ...incidentDetails,
                            referenceLinks: [
                              reference,
                              ...incidentDetails.referenceLinks
                            ]
                          })
                          setReference('')
                        } else {
                          setAddButtonDisabled(true)
                        }
                      }}
                    />
                  </div>
                </div>
                <div className='flex flex-row flex-wrap gap-[8px]'>
                  {incidentDetails?.referenceLinks.map(
                    (item: any, index: any) => (
                      <div
                        className='flex w-[120px] h-[36px] items-center justify-between p-[6px] rounded-md bg-[#F5F7F9]'
                        key={index}
                      >
                        <div
                          className='flex gap-[10px] items-center cursor-pointer'
                          onClick={(e: any) => {
                            e.preventDefault()
                            window.open(item, '_blank', 'noopener,noreferrer')
                          }}
                        >
                          <div>
                            <Link
                              size={25}
                              className='p-[5px] text-[#086cff] rounded-full'
                            />
                          </div>
                          <div>
                            <DDSTypography.Paragraph
                              variant='medium'
                              size='para'
                              color={pallete.colors.black}
                              link={false}
                              className={`flex text-sm text-ellipsis truncate ${store.uiStore.getSidebarExpanded() ? 'max-w-[670px] 2xl:max-w-[950px]' : 'max-w-[790px] 2xl:max-w-[1060px]'}`}
                            >
                              {`Link ${index + 1}`}
                            </DDSTypography.Paragraph>
                          </div>
                        </div>
                        <div className='flex w-[20px]'>
                          <Close
                            size={20}
                            color={pallete.colors.textButtonText}
                            onClick={() => {
                              const updatedLinks = [
                                ...incidentDetails.referenceLinks.slice(
                                  0,
                                  index
                                ),
                                ...incidentDetails.referenceLinks.slice(
                                  index + 1
                                )
                              ]

                              setIncidentDetails({
                                ...incidentDetails,
                                referenceLinks: [...updatedLinks]
                              })
                              if (updatedLinks.includes(reference)) {
                                setAddButtonDisabled(true)
                              } else {
                                setAddButtonDisabled(false)
                              }
                            }}
                            className='cursor-pointer'
                          />
                        </div>
                      </div>
                    )
                  )}
                </div>

                <div>
                  <MultiMedia
                    type='update'
                    updatedSize={totalSize}
                    mediaFiles={mediaFiles}
                    setIncidentDetails={setIncidentDetails}
                    setMediaFiles={setMediaFiles}
                    incidentDetails={incidentDetails}
                    setFilesToRemove={setFilesToRemove}
                    onFileUploadSuccess={(fileUrl) => {
                      setIncidentDetails((prev: any) => ({
                        ...prev,
                        attachedFiles: [...prev.attachedFiles, fileUrl]
                      }))
                    }}
                    disabled={store.uiStore.getGlobalLoader()}
                    attachedFilesLength={incidentDetails?.attachedFiles?.length}
                  />
                </div>
              </div>
              <div className='w-[35%]'>
                <div
                  style={{
                    backgroundColor: pallete.colors.surface6,
                    padding: 24,
                    borderRadius: 10
                  }}
                >
                  <DDSTypography.Title
                    variant='bold'
                    type='h5'
                    style={{ marginBottom: 12 }}
                    color={pallete.colors.textDark4}
                  >
                    <span>{IncidentConstants.WHATS_GOING_ON}</span>
                  </DDSTypography.Title>
                  <DDSTypography.Paragraph
                    variant='medium'
                    size='para'
                    color={pallete.colors.textDark6}
                  >
                    {IncidentConstants.WHATS_GOING_ON_DESC}
                  </DDSTypography.Paragraph>
                </div>
              </div>
            </div>
          </div>
          <div className='flex items-end justify-end relative'>
            <hr className='w-full h-[1px] flex absolute top-0' />
            <div className='flex items-end justify-end px-[20px] py-[20px] gap-x-[12px]'>
              <Button
                id='cancel-incident'
                type='outline'
                size='medium'
                label='Cancel'
                onClick={() => {
                  const route = replaceUrl(
                    routeConstants.MANAGE_INCIDENTS,
                    store.scopeStore.getScope()['organization-id'] as string,
                    store.scopeStore.getScope()['project-id'] as string,
                    store.scopeStore.getScope()['environment-id'] as string
                  ).replace(':incidentId', String(incidentId))

                  const queryParams = { tab: '1' }
                  navigate({
                    pathname: route,
                    search: `?${createSearchParams(queryParams)}`
                  })
                }}
              />
              <Button
                id='update-incident'
                size='medium'
                label='Update Incident'
                onClick={handleUpdateIncident}
                disabled={
                  loading ||
                  store.uiStore.getGlobalLoader() ||
                  isButtonDisabled ||
                  store.userStore.getNoActiveSubscription()
                }
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SecureComponent(
  observer(UpdateIncident),
  AccessRoleConstants.MEMBER_LEVEL
)
